// app/ningenie/components/WakeLockProvider.tsx
// baby: automatically manages screen wake lock for NinGenie PWA to prevent screen dimming

'use client'

import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react'

interface WakeLockContextType {
  wakeLock: WakeLockSentinel | null
  isSupported: boolean
}

const WakeLockContext = createContext<WakeLockContextType>({
  wakeLock: null,
  isSupported: false
})

export const useWakeLock = () => useContext(WakeLockContext)

interface WakeLockProviderProps {
  children: ReactNode
}

export const WakeLockProvider = ({ children }: WakeLockProviderProps) => {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const isSupportedRef = useRef<boolean>(false)

  // baby: check if Screen Wake Lock API is supported
  useEffect(() => {
    isSupportedRef.current = 'wakeLock' in navigator && 'request' in navigator.wakeLock
  }, [])

  // baby: request wake lock when page becomes visible
  const requestWakeLock = async () => {
    if (!isSupportedRef.current) return

    try {
      // baby: release existing wake lock if any
      if (wakeLockRef.current) {
        wakeLockRef.current.release()
        wakeLockRef.current = null
      }

      wakeLockRef.current = await navigator.wakeLock.request('screen')
      console.log('[WakeLock] Screen wake lock acquired')

      // baby: handle wake lock release
      wakeLockRef.current.addEventListener('release', () => {
        console.log('[WakeLock] Screen wake lock released')
        wakeLockRef.current = null
      })
    } catch (err) {
      console.error('[WakeLock] Failed to acquire wake lock:', err)
    }
  }

  // baby: release wake lock
  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release()
      wakeLockRef.current = null
      console.log('[WakeLock] Screen wake lock manually released')
    }
  }

  // baby: handle visibility change - restore wake lock when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[WakeLock] Tab became visible, requesting wake lock')
        await requestWakeLock()
      } else {
        console.log('[WakeLock] Tab became hidden, wake lock will auto-release')
        // baby: wake lock automatically releases when tab is hidden, no need to manually release
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // baby: initial wake lock request when component mounts and page is visible
    if (document.visibilityState === 'visible') {
      requestWakeLock()
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      releaseWakeLock()
    }
  }, [])

  // baby: handle page unload to clean up wake lock
  useEffect(() => {
    const handleBeforeUnload = () => {
      releaseWakeLock()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      releaseWakeLock()
    }
  }, [])

  return (
    <WakeLockContext.Provider
      value={{
        wakeLock: wakeLockRef.current,
        isSupported: isSupportedRef.current
      }}
    >
      {children}
    </WakeLockContext.Provider>
  )
}
